import React from 'react';
import { Box, ButtonBase, Skeleton, Stack, Typography } from '@mui/material';
import MoreHorizRounded from '@mui/icons-material/MoreHorizRounded';
import { ActionMenu, type ActionMenuItem } from '../inputs/ActionMenu';
import { ToolbarButton } from '../inputs/ToolbarButton';
import { StatusChip, type StatusTone } from './StatusChip';


export interface EntityTileButton {
  key: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  /** `brand` for the thing people most often do from here; `outlined` for the rest. */
  variant?: 'brand' | 'outlined';
  disabled?: boolean;
  loading?: boolean;
}

export interface EntityTileProps {
  title: string;
  /** A line or two on what it does. */
  description?: string | null;
  /** A short fact under the title: a category, an owner, "Created 8 days ago". */
  meta?: string | null;
  /** A small icon in a tinted square. */
  icon?: React.ReactNode;
  /** Its state in a word, when that matters: "Live", "Not set up yet". */
  status?: { tone: StatusTone; label: string } | null;
  /**
   * Said in the figures' place when there are none yet — "Not switched on in
   * this workspace yet" — so every card in a grid keeps the same shape.
   */
  footnote?: string | null;
  /** What opening it does. Leave out for something that cannot be opened yet. */
  onOpen?: () => void;
  /** Less common actions — rename, delete — behind a ⋯ button. */
  actions?: ActionMenuItem[];
  /**
   * The one or two things done most often, as buttons across the foot of
   * the card — "Run now", "Schedule". Kept outside the part that opens it.
   */
  buttons?: EntityTileButton[];
  /** Not here yet: the card's outline, shimmering, so the grid holds its shape. */
  loading?: boolean;
}

/**
 * One thing you can open, as a card: an agent, a board, a workspace.
 *
 * An icon, its name, a line on what it does, its state, and a few figures,
 * with the less common actions behind ⋯. Clicking the card opens it; the menu
 * sits beside the clickable area rather than inside it, so each stays one
 * control for a keyboard and a screen reader.
 *
 * For picking one of several options, use `Tile` / `TilePicker` instead.
 */
export function EntityTile({
  title, description, meta, icon, status, footnote, onOpen, actions = [], buttons = [], loading = false,
}: EntityTileProps) {
  if (loading) {
    return (
      <Box
        aria-hidden
        sx={(t) => ({ p: 2, borderRadius: `${t.ds.radius.md}px`, border: `1px solid ${t.tokens.border.subtle}`,
          bgcolor: t.tokens.surface.raised })}
      >
        <Stack direction="row" spacing={1.5}>
          <Skeleton variant="rounded" sx={(t) => ({ width: t.ds.sizes.control.lg, height: t.ds.sizes.control.lg, flexShrink: 0 })} />
          <Stack sx={{ flex: 1 }}>
            <Skeleton variant="text" width="45%" />
            <Skeleton variant="text" width="25%" />
            <Skeleton variant="text" width="90%" />
          </Stack>
        </Stack>
        <Skeleton variant="rounded" height={40} sx={{ mt: 1.5 }} />
      </Box>
    );
  }
  const openable = Boolean(onOpen);
  const body = (
    <Stack spacing={1.25} useFlexGap sx={{ width: '100%', minWidth: 0, flex: 1 }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0, pr: actions.length ? 4 : 0 }}>
        {icon ? (
          <Box
            aria-hidden
            sx={(t) => ({
              width: t.ds.sizes.control.lg,
              height: t.ds.sizes.control.lg,
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              borderRadius: `${t.ds.radius.sm}px`,
              bgcolor: t.tokens.brand.primarySoft,
              color: t.tokens.brand.primary,
            })}
          >
            {icon}
          </Box>
        ) : null}
        <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
          {/* One line of text: a long name wraps and the chip follows the last
              word, rather than being pushed onto a line of its own. */}
          <Typography variant="subtitle1" component="span" sx={(t) => ({ color: t.tokens.text.primary })}>
            {title}
            {status ? (
              <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle', ml: 1 }}>
                <StatusChip tone={status.tone} label={status.label} />
              </Box>
            ) : null}
          </Typography>
          {meta ? (
            <Typography variant="caption" component="span" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              {meta}
            </Typography>
          ) : null}
          {description ? (
            <Typography
              variant="body2"
              component="span"
              title={description}
              sx={(t) => ({
                color: t.tokens.text.secondary,
                pt: 0.25,
                // Three lines at most, so tiles in a row stay the same height.
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              })}
            >
              {description}
            </Typography>
          ) : null}
        </Stack>
      </Stack>

      {footnote ? (
        // Sits at the foot of the card, whatever is above it, so the line runs
        // straight across a row of cards.
        <Box sx={(t) => ({ mt: 'auto', pt: 1.25, borderTop: `1px solid ${t.tokens.border.subtle}` })}>
          <Typography variant="body2" component="span" sx={(t) => ({ display: 'block', color: t.tokens.text.tertiary, py: 1.25 })}>
            {footnote}
          </Typography>
        </Box>
      ) : null}
    </Stack>
  );

  return (
    <Box
      sx={(t) => ({
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: `${t.ds.radius.md}px`,
        border: `1px solid ${t.tokens.border.subtle}`,
        bgcolor: t.tokens.surface.raised,
        transition: `border-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
        '&:hover': openable ? { borderColor: t.tokens.border.strong } : undefined,
      })}
    >
      {openable ? (
        <ButtonBase
          onClick={onOpen}
          aria-label={`Open ${title}`}
          sx={(t) => ({
            width: '100%',
            flex: 1,
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'flex-start',
            textAlign: 'left',
            p: 2,
            borderRadius: `${t.ds.radius.md}px`,
            '&.Mui-focusVisible': { boxShadow: t.tokens.shadow.focusRing },
          })}
        >
          {body}
        </ButtonBase>
      ) : (
        <Box sx={{ p: 2, flex: 1, display: 'flex' }}>{body}</Box>
      )}
      {buttons.length ? (
        <Stack direction="row" spacing={1} sx={{ px: 2, pb: 2 }}>
          {buttons.map((button) => (
            <Box key={button.key} sx={{ flex: 1, '& > *': { width: '100%' } }}>
              <ToolbarButton
                variant={button.variant === 'brand' ? 'brand' : 'outlined'}
                icon={button.icon}
                onClick={button.onClick}
                disabled={button.disabled}
                loading={button.loading}
              >
                {button.label}
              </ToolbarButton>
            </Box>
          ))}
        </Stack>
      ) : null}
      {actions.length ? (
        <Box sx={(t) => ({ position: 'absolute', top: t.spacing(1), right: t.spacing(1) })}>
          <ActionMenu items={actions} icon={<MoreHorizRounded fontSize="small" />} aria-label={`More for ${title}`} />
        </Box>
      ) : null}
    </Box>
  );
}
